import { useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

import type { Socket, ISession } from '../shared/interfaces';

type IUseInitializeSocket = (args: { socketUrl: string }) => {
  socket: Socket | undefined;
  initializeSocket: (args: ISession) => void;
  disconnectSocket: () => void;
};

export const useInitializeSocket: IUseInitializeSocket = ({ socketUrl }) => {
  const socketRef = useRef<Socket | null>(null);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      if (socketRef.current.disconnect) {
        socketRef.current.disconnect();
      }

      socketRef.current = null;
    }
  }, [socketRef]);

  const initializeSocket = useCallback(
    ({ token }: ISession) => {
      if (socketRef.current) {
        disconnectSocket();
      }

      if (token) {
        socketRef.current = io(socketUrl, {
          reconnectionDelayMax: 10000,
          // EBPlay patch: keep Socket.IO's default transport order (long-polling
          // first, then upgrade to WebSocket once connected). Do NOT set this back
          // to ['websocket']: networks that strip the Upgrade/Connection headers —
          // e.g. the Cegedim health-sector DSL used by many Forja users — then fail
          // the handshake forever with {"code":3,"message":"Bad request"} and never
          // receive a single notification. ['websocket', 'polling'] would NOT help
          // either: socket.io-client is pinned at 4.7.2 here and the option that
          // falls back to the next transport, tryAllTransports, only exists from
          // 4.8.0 onwards. Healthy networks still end up on WebSocket via the
          // regular upgrade probe.
          transports: ['polling', 'websocket'],
          auth: {
            token: `${token}`,
          },
        });

        socketRef.current.on('connect_error', (error: any) => {
          // eslint-disable-next-line no-console
          console.error(error.message);
        });
      }
    },
    [socketRef, disconnectSocket]
  );

  return {
    socket: socketRef.current,
    initializeSocket,
    disconnectSocket,
  };
};
